import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { AudioSettingsPanel } from '@/components/AudioSettingsPanel';
import { PWAInstalledBadge } from '@/components/PWAInstallBanner';
import { isPWAInstalled, getInstallInstructions, isIOS } from '@/lib/pwa';
import {
  ArrowLeft, Bell, Smartphone, Download, Monitor, Share,
  CheckCircle
} from 'lucide-react';
import AppFooter from '@/components/AppFooter';

export default function NotificationSettings() {
  const installed = isPWAInstalled();
  const instructions = getInstallInstructions();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-emerald-600 text-white px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/toc">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-bold">Notification Settings</h1>
            <p className="text-sm text-emerald-100">Configure alerts for new positions</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 space-y-6">
        {/* PWA Install Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Smartphone className="h-5 w-5 text-emerald-600" />
                Install ExpressSub App
              </span>
              <PWAInstalledBadge />
            </CardTitle>
            <CardDescription>
              Install ExpressSub for the most reliable notifications
            </CardDescription>
          </CardHeader>
          <CardContent>
            {installed ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-emerald-800">ExpressSub is installed!</p>
                  <p className="text-sm text-emerald-700">You're using the app version for the best experience.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-slate-600">
                  Installing ExpressSub as an app gives you:
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                    <span>More reliable push notifications</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                    <span>Quick access from your home screen</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                    <span>Full-screen experience without browser UI</span>
                  </li>
                </ul>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {instructions.platform === 'iOS' ? (
                      <Share className="h-5 w-5 text-blue-600" />
                    ) : instructions.platform === 'Android' ? (
                      <Download className="h-5 w-5 text-blue-600" />
                    ) : (
                      <Monitor className="h-5 w-5 text-blue-600" />
                    )}
                    <span className="font-medium text-blue-800">How to install on {instructions.platform}:</span>
                  </div>
                  <ol className="space-y-1 text-sm text-blue-700">
                    {instructions.steps.map((step, index) => (
                      <li key={step}>{index + 1}. {step}</li>
                    ))}
                  </ol>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Audio Settings */}
        <AudioSettingsPanel />

        {/* Additional Tips */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bell className="h-5 w-5 text-amber-600" />
              Notification Tips
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="bg-amber-50 rounded-lg p-4">
                <h4 className="font-medium text-amber-900 mb-1">Turn Off Silent Mode</h4>
                <p className="text-sm text-amber-800">
                  {isIOS() 
                    ? "Check the physical switch on the side of your iPhone - orange means silent."
                    : "Make sure Do Not Disturb is off and notification volume is up."
                  }
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-1">Keep the App Open</h4>
                <p className="text-sm text-blue-800">
                  For real-time alerts, keep ExpressSub open in a browser tab during school hours.
                </p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-medium text-emerald-900 mb-1">Allow Notifications</h4>
                <p className="text-sm text-emerald-800">
                  When your browser asks to send notifications, tap "Allow" for instant alerts.
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <h4 className="font-medium text-purple-900 mb-1">Test Your Sound</h4>
                <p className="text-sm text-purple-800">
                  Use the preview buttons above to make sure you can hear your chosen alert.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Back to Dashboard */}
        <div className="text-center pb-8">
          <Link to="/toc">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </main>
      <AppFooter role="toc" />
    </div>
  );
}
